import { useCallback, useEffect, useRef } from 'react';

// BE #642: a submission that hangs before a job exists has no terminal state, so
// the turn reads "still processing" forever. No job means nothing was debited yet,
// so after this long the turn is closed out on the client and the composer freed.
// Turns with a job_id are excluded: they have their own polling and refund path.
export const STALLED_TURN_MS = 90000;
export const STALLED_TURN_NOTICE =
  "This is taking longer than expected. You weren't charged — please try again.";

function stallSignature(message, processingId) {
  if (message?.role !== 'assistant' || message.jobId || message.stalled) return null;
  const pending = message.id === processingId || message.jobStatus === 'processing';
  return pending ? `${message.content}|${message.jobStatus}` : null;
}

function firstSeenAt(message, now) {
  const createdAt = Date.parse(message.createdAt);
  return Number.isFinite(createdAt) ? Math.min(createdAt, now) : now;
}

export default function useStalledChatTurns({
  messages,
  processingId,
  updateAssistantMessage,
  onAbandon,
}) {
  const startedAt = useRef(new Map());
  const abandoned = useRef(new Set());

  useEffect(() => {
    const now = Date.now();
    const tracked = new Map();
    const timers = [];
    messages.forEach((message) => {
      const signature = stallSignature(message, processingId);
      if (!signature) return;
      const previous = startedAt.current.get(message.id);
      // A changed signature means the turn progressed, so the clock restarts.
      const since = !previous
        ? firstSeenAt(message, now)
        : previous.signature === signature ? previous.since : now;
      tracked.set(message.id, { signature, since });
      timers.push(setTimeout(() => {
        if (message.id === processingId) {
          abandoned.current.add(message.id);
          onAbandon();
        }
        updateAssistantMessage(message.id, { content: STALLED_TURN_NOTICE, stalled: true });
      }, Math.max(0, since + STALLED_TURN_MS - now)));
    });
    startedAt.current = tracked;
    return () => timers.forEach(clearTimeout);
  }, [messages, onAbandon, processingId, updateAssistantMessage]);

  return useCallback((messageId) => abandoned.current.has(messageId), []);
}
